import { useCallback, useEffect, useState } from "react";
import { formatBytes } from "@saucer/core";
import type { SaucerFile, SearchHit, Usage } from "@saucer/core";
import { api, cachedFiles, rememberFiles } from "../api.js";
import { useUploads } from "../use-uploads.js";
import { Dropzone } from "../components/dropzone.js";
import { UploadList } from "../components/upload-list.js";
import { FileList } from "../components/file-list.js";
import { LinkBar } from "../components/link-bar.js";
import { SearchBox } from "../components/search-box.js";
import { LiveFeed } from "../components/live-feed.js";
import { useLive } from "../use-live.js";
import { navigate } from "../router.js";
import type { Session } from "../app.js";

/** One server page, and as much as the cached copy keeps for the next visit. */
const PAGE_SIZE = 60;

export function Dashboard({
  session,
  onChange,
}: {
  session: NonNullable<Session>;
  onChange: () => Promise<void>;
}) {
  // The last page this browser saw, painted before the request goes out. The
  // list that lands a moment later replaces it wholesale.
  const [cached] = useState(cachedFiles);
  const [files, setFiles] = useState<SaucerFile[]>(() => cached?.files ?? []);
  const [cursor, setCursor] = useState<string | null>(null);
  const [latest, setLatest] = useState<SaucerFile | null>(null);
  const [loading, setLoading] = useState(cached === null);
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  // The totals ride along with the first page, so the header costs no request
  // of its own.
  const [usage, setUsage] = useState<Usage | null>(cached?.usage ?? null);
  const events = useLive(true);

  const load = useCallback(async (before?: string) => {
    const page = await api.listFiles(before);
    setFiles((current) => (before ? [...current, ...page.files] : page.files));
    setCursor(page.nextCursor);
    if (page.usage) setUsage(page.usage);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Keep the cached copy in step with what is on screen, so an upload or a
  // delete does not flash back into the list on the next visit.
  useEffect(() => {
    if (loading) return;
    rememberFiles({ files: files.slice(0, PAGE_SIZE), usage });
  }, [files, usage, loading]);

  const { jobs, add, dismiss } = useUploads(
    useCallback(
      (file: SaucerFile) => {
        setLatest(file);
        setFiles((current) => [file, ...current]);
        setUsage((current) =>
          current ? { ...current, files: current.files + 1, bytes: current.bytes + file.size } : current,
        );
        void onChange();
      },
      [onChange],
    ),
  );

  return (
    <main className="stack dashboard">
      <Dropzone onFiles={add} />
      <UploadList jobs={jobs} onDismiss={dismiss} />
      <LiveFeed events={events} />
      {latest ? <LinkBar url={latest.url} label={`${latest.filename} is up`} /> : null}

      <SearchBox onResults={setHits} />

      <section className="stack stack--tight">
        <div className="section-head">
          <h2>{hits ? `Matches (${hits.length})` : "Your files"}</h2>
          {usage ? <Quota usage={usage} /> : <span className="meta">…</span>}
        </div>

        {loading ? (
          <p className="meta">Loading…</p>
        ) : (
          <FileList
            files={hits ? hits.map((hit) => hit.file) : files}
            onChanged={(file) =>
              setFiles((current) => current.map((row) => (row.id === file.id ? file : row)))
            }
            onDeleted={(id) => {
              // A hit can be an older file that was never paged in, so the
              // totals and the visible list both have to look in either place.
              const gone =
                files.find((row) => row.id === id) ?? hits?.find((hit) => hit.file.id === id)?.file;
              setFiles((current) => current.filter((row) => row.id !== id));
              setHits((current) => current?.filter((hit) => hit.file.id !== id) ?? null);
              if (gone) {
                setUsage((totals) =>
                  totals ? { ...totals, files: totals.files - 1, bytes: totals.bytes - gone.size } : totals,
                );
              }
              setLatest((current) => (current?.id === id ? null : current));
              void onChange();
            }}
          />
        )}

        {cursor && !hits ? (
          <button className="button" type="button" onClick={() => void load(cursor)}>
            Load older files
          </button>
        ) : null}
      </section>
    </main>
  );
}


/**
 * How full the account is. Only shown once it is worth knowing — under half
 * full this is just a byte count, and a progress bar that is always nearly
 * empty teaches people to ignore progress bars.
 */
function Quota({ usage }: { usage: Usage }) {
  const share = usage.quotaBytes > 0 ? usage.bytes / usage.quotaBytes : 0;
  const tight = share >= 0.5;
  const full = share >= 0.9;

  return (
    <span className={`quota${tight ? " quota--tight" : ""}${full ? " quota--full" : ""}`}>
      {tight ? (
        <span className="quota__track" aria-hidden="true">
          <span className="quota__fill" style={{ inlineSize: `${Math.min(100, share * 100)}%` }} />
        </span>
      ) : null}
      <span className="meta">
        {usage.files} {usage.files === 1 ? "file" : "files"} · {formatBytes(usage.bytes)}
        {tight ? ` of ${formatBytes(usage.quotaBytes)}` : ""}
      </span>
      {full ? (
        <a className="quota__cta" href="/pricing" onClick={goPricing}>
          Get more room
        </a>
      ) : null}
    </span>
  );
}

const goPricing = (event: React.MouseEvent) => {
  event.preventDefault();
  navigate("/pricing");
};
